import { StoredQueryOptions } from '@/models/item.models';
import { useOrganizationStore } from '@/store/organizationStore';
import dayjs from '@/utils/dayjs';
import { DateTimeRange } from '@/models/common.models';
import {
  BinaryOperator,
  Filter,
  TimeDimension,
  UnaryOperator,
} from '@cubejs-client/core';
import {
  DSCubeDimensionName,
  DSCubeTableName,
  DSCubeQuery as Query,
} from '@/models/cube.models';
import {
  CurrentProgressChartType,
  IItemsTableWorkItem,
  ITimeSpentWorkItem,
} from '@/models/data.models';
import { isNil } from 'lodash-es';
import {
  Board,
  CustomFieldConditions,
  AdvancedFilterGroup,
  ConditionOperator,
  ConditionSet,
} from '@/api';
import { LaneType } from '@/api';
import { decodeBase64 } from '@/utils/encode-decode';

export function completedOverviewQuery(
  organizationId: string,
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      // Item Count measures start
      'Workitems.completedCount',
      'Workitems.notCompletedCount',
      'Workitems.assignedCount',
      'Workitems.notAssignedCount',
      'Workitems.plannedCount',
      'Workitems.notPlannedCount',
      'Workitems.estimatedCount',
      'Workitems.unEstimatedCount',
      'Workitems.pointedCount',
      'Workitems.unPointedCount',
      // Item Count measures end

      // Item Time Spent measures start
      'Workitems.completedTimeSpentSeconds',
      'Workitems.notCompletedTimeSpentSeconds',
      'Workitems.assignedTimeSpentSeconds',
      'Workitems.notAssignedTimeSpentSeconds',
      'Workitems.plannedTimeSpentSeconds',
      'Workitems.notPlannedTimeSpentSeconds',
      'Workitems.estimatedTimeSpentSeconds',
      'Workitems.unEstimatedTimeSpentSeconds',
      'Workitems.pointedTimeSpentSeconds',
      'Workitems.unPointedTimeSpentSeconds',
      // Item Time Spent measures end

      // Item Point measures start
      'Workitems.plannedPointEffort',
      'Workitems.notPlannedPointEffort',
      'Workitems.notAssignedPointEffort',
      'Workitems.assignedPointEffort',
      'Workitems.completedPointEffort',
      'Workitems.notCompletedPointEffort',
      // Item Point measures end

      // Item Time Estimate measures start
      'Workitems.plannedEstimate',
      'Workitems.plannedEstimateSD',
      'Workitems.notPlannedEstimate',
      'Workitems.notPlannedEstimateSD',
      'Workitems.assignedEstimate',
      'Workitems.assignedEstimateSD',
      'Workitems.notAssignedEstimate',
      'Workitems.notAssignedEstimateSD',
      'Workitems.completedEstimate',
      'Workitems.completedEstimateSD',
      'Workitems.notCompletedEstimate',
      'Workitems.notCompletedEstimateSD',
      // Item Time Estimate measures end
    ],
    order: {
      'Workitems.dateupdated': 'asc',
    },
    timezone,
    filters: getCubeFilters(organizationId, filters),
  };
}

export function completedOverviewLaneQuery(
  organizationId: string,
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'Workitems.totalEstimation',
      'Workitems.totalEstimationSD',
      'Workitems.totalPointed',
      'Workitems.count',
      'Workitems.totalTimeSpentSeconds',
    ],
    order: {
      'Workitems.totalEstimation': 'desc',
    },
    dimensions: ['Workitems.laneId'],
    timezone,
    filters: getCubeFilters(organizationId, filters),
  };
}

// Completed is in done lane
// everything else is not completed
export function trendingCompletedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'LaneTransactions.itemCS', // Item count
      'LaneTransactions.estimateCS', // Time estimate
      'LaneTransactions.pointEffortCS', // Point effort
      'LaneTransactions.timeCS', // Time spent seconds
      'LaneTransactions.estimateSdCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'LaneTransactions.transactionDate'
    ),
    dimensions: ['LaneTransactions.laneId'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions'),
  };
}

export function trendingTimeEstimateQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'EstimateTransactions.estimateCS',
      'EstimateTransactions.noEstimateCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'EstimateTransactions.transactionDate'
    ),
    timezone,
    filters: getCubeFilters(organizationId, filters),
  };
}

export function trendingPointEstimatedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'PointEffortTransactions.pointEffortCS',
      'PointEffortTransactions.noPointEffortCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'PointEffortTransactions.transactionDate'
    ),
    timezone,
    filters: getCubeFilters(organizationId, filters),
  };
}

export function trendingAssignedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'AssigneeTransactions.itemCS',
      'AssigneeTransactions.estimateCS',
      'AssigneeTransactions.pointEffortCS',
      'AssigneeTransactions.timeCS',
      'AssigneeTransactions.estimateSdCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'AssigneeTransactions.transactionDate'
    ),
    dimensions: ['AssigneeTransactions.assigneeStatus'],
    timezone,
    filters: getCubeFilters(organizationId, filters),
  };
}

export function trendingPlannedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'BoardTransactions.itemCS',
      'BoardTransactions.estimateCS',
      'BoardTransactions.pointEffortCS',
      'BoardTransactions.timeCS',
      'BoardTransactions.estimateSdCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'BoardTransactions.transactionDate'
    ),
    dimensions: ['BoardTransactions.plannedStatus'],
    timezone,
    filters: getCubeFilters(organizationId, filters),
  };
}

export function trendingLaneQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'LaneTransactions.itemCS', // Item count cumulative
      'LaneTransactions.estimateCS', // Time estimate cumulative
      'LaneTransactions.pointEffortCS', // Point effort cumulative
      'LaneTransactions.timeCS', // Time spent seconds cumulative
      'LaneTransactions.estimateSdCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'LaneTransactions.transactionDate'
    ),
    dimensions: ['LaneTransactions.laneId'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions'),
  };
}

export function throughputQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  const doneLaneId = organizationStore.activeOrganization?.lanes.find(
    (l) => l.type === LaneType.DONE
  )?.id;
  if (!doneLaneId) {
    throw new Error('Done status id not found');
  }

  return {
    measures: [
      'LaneTransactions.itemS', // Item count
      'LaneTransactions.estimateS', // Time estimate
      'LaneTransactions.pointEffortS', // Point effort
      'LaneTransactions.timeS', // Time spent seconds
      'LaneTransactions.estimateSdS',
    ],
    timezone,
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'LaneTransactions.transactionDate'
    ),
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions', [
      {
        member: 'LaneTransactions.laneId',
        operator: 'equals',
        values: [doneLaneId],
      },
    ]),
  };
}

export function churnCycleQuery(
  organizationId: string,
  cycles: Board[],
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  // find the earliest date in the cycles array
  const earliestDate = cycles.reduce<string>((acc, cycle) => {
    if (!acc) return cycle.startDate ?? '';
    return dayjs(cycle.startDate).isBefore(acc) ? cycle.startDate ?? acc : acc;
  }, '');
  // find the latest date in the cycles array
  const latestDate = cycles.reduce<string>((acc, cycle) => {
    if (!acc) return cycle.endDate ?? '';
    return dayjs(cycle.endDate).isAfter(acc) ? cycle.endDate ?? acc : acc;
  }, '');
  return {
    measures: [
      'PlannedChurnTransactions.itemS',
      'PlannedChurnTransactions.estimateS',
      'PlannedChurnTransactions.estimateSdS',
      'PlannedChurnTransactions.pointEffortS',
      'PlannedChurnTransactions.timeS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      {
        from: dayjs(earliestDate).toISOString(),
        to: dayjs(latestDate).toISOString(),
      },
      'day',
      'PlannedChurnTransactions.transactionDate'
    ),
    dimensions: [
      'PlannedChurnTransactions.churnStatus',
      'PlannedChurnTransactions.boardId',
    ],
    timezone,
    filters: getCubeFilters(
      organizationId,
      filters,
      'PlannedChurnTransactions',
      [
        {
          member: 'PlannedChurnTransactions.boardId',
          operator: 'equals',
          values: cycles.map((c) => c.id),
        },
      ]
    ),
  };
}

export function churnPlannedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'PlannedChurnTransactions.itemS',
      'PlannedChurnTransactions.estimateS',
      'PlannedChurnTransactions.estimateSdS',
      'PlannedChurnTransactions.pointEffortS',
      'PlannedChurnTransactions.timeS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'PlannedChurnTransactions.transactionDate'
    ),
    dimensions: ['PlannedChurnTransactions.churnStatus'],
    timezone,
    filters: getCubeFilters(
      organizationId,
      filters,
      'PlannedChurnTransactions'
    ),
  };
}

export function churnCreatedDeletedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'ItemChurnTransactions.itemS',
      'ItemChurnTransactions.estimateS',
      'ItemChurnTransactions.estimateSdS',
      'ItemChurnTransactions.pointEffortS',
      'ItemChurnTransactions.timeS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'ItemChurnTransactions.transactionDate'
    ),
    dimensions: ['ItemChurnTransactions.churnStatus'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'ItemChurnTransactions'),
  };
}

export function churnAssignedUnassignedQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: [
      'AssigneeChurnTransactions.itemS',
      'AssigneeChurnTransactions.estimateS',
      'AssigneeChurnTransactions.pointEffortS',
      'AssigneeChurnTransactions.timeS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'AssigneeChurnTransactions.transactionDate'
    ),
    dimensions: ['AssigneeChurnTransactions.churnStatus'],
    timezone,
    filters: getCubeFilters(
      organizationId,
      filters,
      'AssigneeChurnTransactions'
    ),
  };
}

export function cycleVelocityQuery(
  organizationId: string,
  cycles: Board[],
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  // find the earliest date in the cycles array
  const earliestDate = cycles.reduce<string>((acc, cycle) => {
    if (!acc) return cycle.startDate ?? '';
    return dayjs(cycle.startDate).isBefore(acc) ? cycle.startDate ?? acc : acc;
  }, '');
  // find the latest date in the cycles array
  const latestDate = cycles.reduce<string>((acc, cycle) => {
    if (!acc) return cycle.endDate ?? '';
    return dayjs(cycle.endDate).isAfter(acc) ? cycle.endDate ?? acc : acc;
  }, '');

  return {
    measures: [
      'LaneTransactions.itemCS', // Item count cumulative
      'LaneTransactions.estimateCS', // Time estimate cumulative
      'LaneTransactions.pointEffortCS', // Point effort cumulative
      'LaneTransactions.timeCS', // Time spent seconds cumulative
      'LaneTransactions.estimateSdCS',
    ],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      {
        from: dayjs(earliestDate).toISOString(),
        to: dayjs(latestDate).toISOString(),
      },
      'day',
      'LaneTransactions.transactionDate'
    ),
    dimensions: ['LaneTransactions.laneId', 'LaneTransactions.boardId'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions', [
      {
        member: 'LaneTransactions.boardId',
        operator: 'equals',
        values: cycles.map((c) => c.id),
      },
    ]),
  };
}

export function leadTimeQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';
  return {
    measures: ['LeadTimeTransactions.timeSpentAvg'],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'LeadTimeTransactions.transactionDate'
    ),
    timezone,
    dimensions: ['LeadTimeTransactions.organizationId'],
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'Workitems.isNotCompleted',
        operator: 'equals',
        values: ['false'],
      },
    ]),
  };
}

export function cycleTimeQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  const newLaneId = organizationStore.activeOrganization?.lanes.find(
    (l) => l.type === LaneType.NEW
  )?.id;

  const doneLaneId = organizationStore.activeOrganization?.lanes.find(
    (l) => l.type === LaneType.DONE
  )?.id;

  if (!newLaneId || !doneLaneId) {
    throw new Error('Missing new or done lane');
  }

  return {
    measures: ['CycleTimeTransactions.timeSpentAvg'],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'CycleTimeTransactions.transactionDate'
    ),
    timezone,
    dimensions: ['CycleTimeTransactions.organizationId'],
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'Workitems.isNotCompleted',
        operator: 'equals',
        values: ['false'],
      },
      {
        member: 'CycleTimeTransactions.laneId',
        operator: 'notEquals',
        values: [newLaneId, doneLaneId],
      },
    ]),
  };
}

export function customCycleTimeQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month',
  fromLaneId: string,
  toLaneId: string,
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  return {
    measures: ['LaneTimeTransactions.timeTakenAvg'],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      dateGranularity,
      'LaneTimeTransactions.transactionDate'
    ),
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'LaneTimeTransactions.startLaneId',
        operator: 'equals',
        values: [fromLaneId],
      },
      {
        member: 'LaneTimeTransactions.endLaneId',
        operator: 'equals',
        values: [toLaneId],
      },
      {
        member: 'Workitems.isNotCompleted',
        operator: 'equals',
        values: ['false'],
      },
    ]),
  };
}

export function customCycleTimeSumsQuery(
  organizationId: string,
  dateRange: DateTimeRange,
  fromLaneId: string,
  toLaneId: string,
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  return {
    measures: [
      'LaneTimeTransactions.timeTakenMin',
      'LaneTimeTransactions.timeTakenMax',
      'LaneTimeTransactions.timeTakenRangeAvg',
    ],
    dimensions: ['LaneTimeTransactions.organizationId'],
    timeDimensions: dateRangeToTimeCubeTimeDimensions(
      dateRange,
      undefined,
      'LaneTimeTransactions.transactionDate'
    ),
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'LaneTimeTransactions.startLaneId',
        operator: 'equals',
        values: [fromLaneId],
      },
      {
        member: 'LaneTimeTransactions.endLaneId',
        operator: 'equals',
        values: [toLaneId],
      },
      {
        member: 'Workitems.isNotCompleted',
        operator: 'equals',
        values: ['false'],
      },
    ]),
  };
}

export function itemTransactionQuery(
  organizationId: string,
  dateRange?: DateTimeRange,
  filters?: StoredQueryOptions
): Query {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  const cubeFilters: Filter[] = [];
  if (dateRange) {
    cubeFilters.push({
      member: 'LaneTransactions.transactionDate',
      operator: 'inDateRange',
      values: [
        dayjs(dateRange.from).format('YYYY-MM-DD'),
        dayjs(dateRange.to).format('YYYY-MM-DD'),
      ],
    });
  }
  return {
    dimensions: [
      'Workitems.title',
      'Workitems.number',
      'LaneTransactions.transactionDate',
      'LaneTransactions.estimate',
      'LaneTransactions.pointEffort',
      'LaneTransactions.timeSpentSeconds',
      'LaneTransactions.laneId',
      'LaneTransactions.transactionType',
      'LaneTransactions.userId',
      'LaneTransactions.organizationId',
      'LaneTransactions.itemId',
      'LaneTransactions.teamId',
      'LaneTransactions.boardId',
      'LaneTransactions.assigneeUsername',
    ],
    order: [['LaneTransactions.transactionDate', 'asc']],
    timezone,
    filters: getCubeFilters(
      organizationId,
      filters,
      'LaneTransactions',
      cubeFilters
    ),
  };
}

// trending progress
export function trendingProgressDoneItemsQuery(
  type: 'completed' | 'remaining',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';
  const doneLanes = organizationStore.activeOrganization.lanes.filter(
    (l) => l.type === LaneType.DONE
  );

  const query: Query = {
    measures: [
      'LaneTransactions.itemCS',
      'LaneTransactions.estimateCS',
      'LaneTransactions.estimateSdCS',
      'LaneTransactions.pointEffortCS',
      'LaneTransactions.timeCS',
    ],
    timeDimensions: [
      {
        dimension: 'LaneTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: [
      'LaneTransactions.laneId',
      'Workitems.title',
      'Workitems.number',
      'Workitems.workTypeId',
    ],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'LaneTransactions.itemCS',
        operator: 'equals',
        values: ['1'],
      },
      {
        member: `LaneTransactions.laneId`,
        operator: type === 'completed' ? 'equals' : 'notEquals',
        values: doneLanes.map((l) => l.id),
      },
    ]),
    order: [['LaneTransactions.transactionDate', 'asc']],
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['LaneTransactions.pointEffortCS'],
    estimate: row['LaneTransactions.estimateCS'],
    estimateRange: row['LaneTransactions.estimateSdCS'],
    timeSpentSeconds: row['LaneTransactions.timeCS'],
  });
  return { query, rowMapper };
}

export function trendingProgressPointedItemsQuery(
  type: 'pointed' | 'notPointed',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: ['PointEffortTransactions.pointEffortSumCS'],
    timeDimensions: [
      {
        dimension: 'PointEffortTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: ['Workitems.title', 'Workitems.number', 'Workitems.workTypeId'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'Workitems.organizationId',
        operator: 'equals',
        values: ['970b8dea-c8b4-44d8-8aaf-febf3f25138a'],
      },
      {
        member:
          type === 'pointed'
            ? `PointEffortTransactions.pointEffortCS`
            : `PointEffortTransactions.noPointEffortCS`,
        operator: 'equals',
        values: ['1'],
      },
    ]),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['PointEffortTransactions.pointEffortSumCS'],
    estimate: 'N/A',
    estimateRange: 'N/A',
    timeSpentSeconds: 'N/A',
  });
  return { query, rowMapper };
}

export function trendingProgressTimeEstimateItemsQuery(
  type: 'estimated' | 'notEstimated',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: [
      'EstimateTransactions.estimateSumCS',
      'EstimateTransactions.estimateSdSumCS',
    ],
    timeDimensions: [
      {
        dimension: 'EstimateTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: ['Workitems.title', 'Workitems.workTypeId', 'Workitems.number'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member:
          type === 'estimated'
            ? `EstimateTransactions.estimateCS`
            : `EstimateTransactions.noEstimateCS`,
        operator: 'equals',
        values: ['1'],
      },
    ]),
  };

  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    estimate: row['EstimateTransactions.estimateSumCS'],
    estimateRange: row['EstimateTransactions.estimateSdSumCS'],
    pointEffort: 'N/A',
    timeSpentSeconds: 'N/A',
  });
  return { query, rowMapper };
}

export function trendingProgressAssignedItemsQuery(
  type: 'assigned' | 'notAssigned',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: [
      'AssigneeTransactions.itemCS',
      'AssigneeTransactions.estimateCS',
      'AssigneeTransactions.estimateSdCS',
      'AssigneeTransactions.pointEffortCS',
      'AssigneeTransactions.timeCS',
    ],
    timeDimensions: [
      {
        dimension: 'AssigneeTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: ['Workitems.title', 'Workitems.number', 'Workitems.workTypeId'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'AssigneeTransactions.itemCS',
        operator: 'equals',
        values: ['1'],
      },
      {
        member: 'AssigneeTransactions.assigneeStatus',
        operator: type === 'assigned' ? 'equals' : 'notEquals',
        values: ['true'],
      },
    ]),
    order: [['PointEffortTransactions.transactionDate', 'asc']],
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['AssigneeTransactions.pointEffortCS'],
    estimate: row['AssigneeTransactions.estimateCS'],
    estimateRange: row['AssigneeTransactions.estimateSdCS'],
    timeSpentSeconds: row['AssigneeTransactions.timeCS'],
  });
  return { query, rowMapper };
}

export function trendingProgressPlanedItemsQuery(
  type: 'planned' | 'notPlanned',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: [
      'BoardTransactions.itemCS',
      'BoardTransactions.estimateCS',
      'BoardTransactions.estimateSdCS',
      'BoardTransactions.pointEffortCS',
      'BoardTransactions.timeCS',
    ],
    timeDimensions: [
      {
        dimension: 'BoardTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: [
      'BoardTransactions.plannedStatus',
      'Workitems.title',
      'Workitems.number',
      'Workitems.workTypeId',
    ],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'BoardTransactions.itemCS',
        operator: 'equals',
        values: ['1'],
      },
      {
        member: 'BoardTransactions.plannedStatus',
        operator: type === 'planned' ? 'equals' : 'notEquals',
        values: ['true'],
      },
    ]),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['BoardTransactions.pointEffortCS'],
    estimate: row['BoardTransactions.estimateCS'],
    estimateRange: row['BoardTransactions.estimateSdCS'],
    timeSpentSeconds: row['BoardTransactions.timeCS'],
  });
  return { query, rowMapper };
}
// get Items for Status query
export function lanesItemsQuery(
  type: 'lane',
  dateRange: DateTimeRange,
  laneId: string,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: [
      'LaneTransactions.itemCS',
      'LaneTransactions.estimateCS',
      'LaneTransactions.estimateSdCS',
      'LaneTransactions.pointEffortCS',
      'LaneTransactions.timeCS',
    ],
    timeDimensions: [
      {
        dimension: 'LaneTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: [
      'LaneTransactions.laneId',
      'Workitems.title',
      'Workitems.number',
      'Workitems.workTypeId',
    ],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions', [
      {
        member: 'LaneTransactions.itemCS',
        operator: 'equals',
        values: ['1'],
      },
      {
        member: 'LaneTransactions.laneId',
        operator: 'equals',
        values: [laneId],
      },
    ]),
    order: [['LaneTransactions.itemCS', 'desc']],
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['LaneTransactions.pointEffortCS'],
    estimate: row['LaneTransactions.estimateCS'],
    estimateRange: row['LaneTransactions.estimateSdCS'],
    timeSpentSeconds: row['LaneTransactions.timeCS'],
  });
  return { query, rowMapper };
}

// burnup/burndown chart items

export function burnCompletedRemainingItemsQuery(
  type: 'completed' | 'remaining',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';
  const doneLanes = organizationStore.activeOrganization.lanes.filter(
    (l) => l.type === LaneType.DONE
  );

  const query: Query = {
    measures: [
      `LaneTransactions.pointEffortS`,
      `LaneTransactions.estimateS`,
      `LaneTransactions.estimateSdS`,
      `LaneTransactions.timeS`,
    ],
    dimensions: ['Workitems.number', 'Workitems.workTypeId', 'Workitems.title'],
    timezone,
    order: [[`LaneTransactions.transactionDate`, 'asc']],

    filters: getCubeFilters(organizationId, filters, 'LaneTransactions', [
      {
        member: `LaneTransactions.transactionDate`,
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: `LaneTransactions.itemS`,
        operator: 'equals',
        values: ['1'],
      },
      {
        member: `LaneTransactions.laneId`,
        operator: type === 'completed' ? 'equals' : 'notEquals',
        values: doneLanes.map((l) => l.id),
      },
    ]),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['LaneTransactions.pointEffortS'],
    estimate: row['LaneTransactions.estimateS'],
    estimateRange: row['LaneTransactions.estimateSdS'],
    timeSpentSeconds: row['LaneTransactions.timeS'],
  });
  return { query, rowMapper };
}

export function customCycleTimeItemsQuery(
  type: 'cycleTime' | 'leadTime',
  dateRange: DateTimeRange,
  fromLaneId: string,
  toLaneId: string,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: ['LaneTimeTransactions.timeTakenAvg'],
    timeDimensions: [
      {
        dimension: 'LaneTimeTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: [
      'Workitems.title',
      'Workitems.number',
      'Workitems.workTypeId',
      'LaneTimeTransactions.timeTaken',
    ],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'Workitems', [
      {
        member: 'LaneTimeTransactions.startLaneId',
        operator: 'equals',
        values: [fromLaneId],
      },
      {
        member: 'LaneTimeTransactions.endLaneId',
        operator: 'equals',
        values: [toLaneId],
      },
      {
        member: 'Workitems.isNotCompleted',
        operator: 'equals',
        values: ['false'],
      },
    ]),
    order: [[`LaneTimeTransactions.transactionDate`, 'asc']],
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    timeSpentSeconds: (
      parseFloat(row['LaneTimeTransactions.timeTakenAvg']) *
      60 *
      60
    ).toString(),
    estimate: 'N/A',
    estimateRange: 'N/A',
    pointEffort: 'N/A',
  });
  return { query, rowMapper };
}

export function throughputAverageCompletedItemsQuery(
  type: 'completed',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';
  const doneLanes = organizationStore.activeOrganization.lanes.filter(
    (l) => l.type === LaneType.DONE
  );

  const query: Query = {
    timeDimensions: [
      {
        dimension: 'LaneTransactions.transactionDate',
        granularity: 'day',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    dimensions: [
      'Workitems.title',
      'Workitems.number',
      'Workitems.workTypeId',
      'LaneTransactions.laneId',
      'LaneTransactions.transactionDate',
      'LaneTransactions.transactionType',
      'LaneTransactions.estimate',
      'LaneTransactions.estimateSd',
      'LaneTransactions.pointEffort',
      'LaneTransactions.timeSpentSeconds',
    ],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions', [
      {
        member: `LaneTransactions.laneId`,
        operator: type === 'completed' ? 'equals' : 'notEquals',
        values: doneLanes.map((l) => l.id),
      },
      {
        member: `LaneTransactions.laneChange`,
        operator: 'equals',
        values: ['1'],
      },
    ]),
    order: [[`LaneTransactions.transactionDate`, 'asc']],
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['LaneTransactions.pointEffort'],
    estimate: row['LaneTransactions.estimate'],
    estimateRange: row['LaneTransactions.estimateSd'],
    timeSpentSeconds: row['LaneTransactions.timeSpentSeconds'],
  });
  return { query, rowMapper };
}

export function burnCompletedRemainingSummaryQuery(
  type: 'completed' | 'remaining',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';
  const doneLanes = organizationStore.activeOrganization.lanes.filter(
    (l) => l.type === LaneType.DONE
  );

  const summaryQuery: Query = {
    measures: [
      'LaneTransactions.itemS',
      'LaneTransactions.estimateS',
      'LaneTransactions.estimateSdS',
      'LaneTransactions.pointEffortS',
      'LaneTransactions.timeS',
    ],
    timeDimensions: [
      {
        dimension: 'LaneTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'LaneTransactions', [
      {
        member: `LaneTransactions.laneId`,
        operator: type === 'completed' ? 'equals' : 'notEquals',
        values: doneLanes.map((l) => l.id),
      },
    ]),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    isSummary: true,
    number: 'summary',
    title: row['LaneTransactions.itemS'],
    workTypeId: '',
    pointEffort: row['LaneTransactions.pointEffortS'],
    estimate: row['LaneTransactions.estimateS'],
    estimateRange: row['LaneTransactions.estimateSdS'],
    timeSpentSeconds: row['LaneTransactions.timeS'],
  });
  return { query: summaryQuery, rowMapper };
}

// churn chart items

export function churnCyclePlannedItemsQuery(
  type: 'added' | 'removed',
  cycle: Board,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: [
      `PlannedChurnTransactions.pointEffortCS`,
      `PlannedChurnTransactions.estimateCS`,
      `PlannedChurnTransactions.estimateSdCS`,
      `PlannedChurnTransactions.timeCS`,
    ],
    dimensions: ['Workitems.number', 'Workitems.workTypeId', 'Workitems.title'],
    timezone,
    order: [[`PlannedChurnTransactions.transactionDate`, 'asc']],

    filters: getCubeFilters(
      organizationId,
      {
        ...filters,
        boardId: [cycle.id],
      },
      'PlannedChurnTransactions',
      [
        {
          member: `PlannedChurnTransactions.transactionDate`,
          operator: 'inDateRange',
          values: [
            dayjs(cycle.startDate).format('YYYY-MM-DD'),
            dayjs(cycle.endDate).format('YYYY-MM-DD'),
          ],
        },
        // only added or removed
        {
          member: `PlannedChurnTransactions.churnStatus`,
          operator: 'equals',
          values: [type],
        },
      ]
    ),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['PlannedChurnTransactions.pointEffortCS'],
    estimate: row['PlannedChurnTransactions.estimateCS'],
    estimateRange: row['PlannedChurnTransactions.estimateSdCS'],
    timeSpentSeconds: row['PlannedChurnTransactions.timeCS'],
  });
  return { query, rowMapper };
}
// Daily churn chart items
export function churnDailyPlannedItemsQuery(
  type: 'added' | 'removed',
  cycle: Board,
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';
  const query: Query = {
    measures: [
      `PlannedChurnTransactions.pointEffortCS`,
      `PlannedChurnTransactions.estimateCS`,
      `PlannedChurnTransactions.estimateSdCS`,
      `PlannedChurnTransactions.timeCS`,
    ],
    dimensions: [
      'Workitems.number',
      'Workitems.title',
      'Workitems.workTypeId',
      'PlannedChurnTransactions.transactionDate',
    ],
    timezone,
    order: [[`PlannedChurnTransactions.transactionDate`, 'asc']],
    filters: getCubeFilters(
      organizationId,
      {
        ...filters,
        boardId: [cycle.id],
      },
      'PlannedChurnTransactions',
      [
        {
          member: `PlannedChurnTransactions.transactionDate`,
          operator: 'inDateRange',
          values: [
            dayjs(dateRange.from).format('YYYY-MM-DD'),
            dayjs(dateRange.to).format('YYYY-MM-DD'),
          ],
        },
        {
          member: `PlannedChurnTransactions.churnStatus`,
          operator: 'equals',
          values: [type],
        },
      ]
    ),
  };

  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['PlannedChurnTransactions.pointEffortCS'],
    estimate: row['PlannedChurnTransactions.estimateCS'],
    estimateRange: row['PlannedChurnTransactions.estimateSdCS'],
    timeSpentSeconds: row['PlannedChurnTransactions.timeCS'],
  });
  return { query, rowMapper };
}

export function churnCycleCreatedDeletedItemsQuery(
  type: 'added' | 'removed',
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: [
      `ItemChurnTransactions.pointEffortS`,
      `ItemChurnTransactions.estimateS`,
      `ItemChurnTransactions.estimateSdS`,
      `ItemChurnTransactions.timeS`,
    ],
    dimensions: ['Workitems.number', 'Workitems.title', 'Workitems.workTypeId'],
    timezone,
    order: [[`ItemChurnTransactions.transactionDate`, 'asc']],
    timeDimensions: [
      {
        dimension: 'ItemChurnTransactions.transactionDate',
        dateRange: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ],
    filters: getCubeFilters(organizationId, filters, 'ItemChurnTransactions', [
      {
        member: `ItemChurnTransactions.churnStatus`,
        operator: 'equals',
        values: [type],
      },
    ]),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['ItemChurnTransactions.pointEffortS'],
    estimate: row['ItemChurnTransactions.estimateS'],
    estimateRange: row['ItemChurnTransactions.estimateSdS'],
    timeSpentSeconds: row['ItemChurnTransactions.timeS'],
  });
  return { query, rowMapper };
}

// velocity chart items

export function velocityCycleCommittedCompletedItemsQuery(
  type: 'committed' | 'completed',
  cycle: Board,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const doneLanes = organizationStore.activeOrganization.lanes.filter(
    (l) => l.type === LaneType.DONE
  );

  const query: Query = {
    measures: [
      `LaneTransactions.pointEffortCS`,
      `LaneTransactions.estimateCS`,
      `LaneTransactions.estimateSdCS`,
      `LaneTransactions.timeCS`,
    ],
    dimensions: ['Workitems.number', 'Workitems.title', 'Workitems.workTypeId'],
    timezone,
    order: [[`LaneTransactions.transactionDate`, 'asc']],
    filters: getCubeFilters(
      organizationId,
      {
        ...filters,
        boardId: [cycle.id],
        laneId: type === 'committed' ? undefined : doneLanes.map((l) => l.id),
      },
      'LaneTransactions',
      [
        {
          member: `LaneTransactions.transactionDate`,
          operator: 'inDateRange',
          values: [
            dayjs('2000-01-01').format('YYYY-MM-DD'),
            dayjs(
              type === 'committed' ? cycle.startDate : cycle.endDate
            ).format('YYYY-MM-DD'),
          ],
        },
        // exclude items that were removed
        {
          member: `LaneTransactions.itemCS`,
          operator: 'equals',
          values: ['1'],
        },
      ]
    ),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['LaneTransactions.pointEffortCS'],
    estimate: row['LaneTransactions.estimateCS'],
    estimateRange: row['LaneTransactions.estimateSdCS'],
    timeSpentSeconds: row['LaneTransactions.timeCS'],
  });
  return { query, rowMapper };
}

// Current Progress chart items
export function currentProgressItemsQuery(
  type: CurrentProgressChartType,
  madeProgress: boolean, // for example, so we know if it's completed or NOT completed
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => IItemsTableWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const additionalFilters: Filter[] = [];
  if (type === CurrentProgressChartType.COMPLETED) {
    additionalFilters.push({
      member: 'Workitems.isNotCompleted',
      operator: 'equals',
      values: [madeProgress ? 'false' : 'true'],
    });
  } else if (type === CurrentProgressChartType.ASSIGNED) {
    additionalFilters.push({
      member: 'Workitems.assigneeUsername',
      operator: madeProgress ? 'set' : 'notSet',
    });
  } else if (type === CurrentProgressChartType.PLANNED) {
    additionalFilters.push({
      member: madeProgress
        ? 'Workitems.plannedCount'
        : 'Workitems.notPlannedCount',
      operator: 'equals',
      values: ['1'],
    });
  } else if (type === CurrentProgressChartType.POINT_ESTIMATED) {
    additionalFilters.push({
      member: 'Workitems.pointEffort',
      operator: madeProgress ? 'notEquals' : 'equals',
      values: ['0'],
    });
  } else if (type === CurrentProgressChartType.TIME_ESTIMATED) {
    additionalFilters.push({
      member: 'Workitems.estimate',
      operator: madeProgress ? 'notEquals' : 'equals',
      values: ['0'],
    });
  }

  const orderAttribute = ((): DSCubeDimensionName => {
    if (type === CurrentProgressChartType.COMPLETED && madeProgress) {
      return 'Workitems.completedAt';
    } else if (type === CurrentProgressChartType.ASSIGNED && madeProgress) {
      return 'Workitems.assignedAt';
    } else {
      return 'Workitems.dateAdded';
    }
  })();

  const query: Query = {
    dimensions: [
      'Workitems.number',
      'Workitems.estimate',
      'Workitems.estimateSD',
      'Workitems.timeSpentSeconds',
      'Workitems.pointEffort',
      'Workitems.title',
      'Workitems.workTypeId',
      'Workitems.completedAt',
      'Workitems.assignedAt',
      'Workitems.dateAdded',
    ],
    timezone,
    order: {
      [orderAttribute]: 'desc',
    },
    filters: getCubeFilters(
      organizationId,
      filters,
      'Workitems',
      additionalFilters
    ),
  };
  /** helper to map queried rows to IItemsTableWorkItem */
  const rowMapper: (row: Record<string, string>) => IItemsTableWorkItem = (
    row
  ) => ({
    type: 'item',
    number: row['Workitems.number'],
    title: row['Workitems.title'],
    workTypeId: row['Workitems.workTypeId'],
    pointEffort: row['Workitems.pointEffort'],
    estimate: row['Workitems.estimate'],
    estimateRange: row['Workitems.estimateSD'],
    timeSpentSeconds: row['Workitems.timeSpentSeconds'],
  });
  return { query, rowMapper };
}

// User Time Tracking - Item total time spent by user (from time entries)
export function userCompletedTimeItemTimeSpentTotalQuery(
  organizationId: string,
  assigneeUsername: string,
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  return {
    measures: ['TimeEntries.totalLengthSeconds'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'TimeEntries', [
      {
        member: 'TimeEntries.authorUsername',
        operator: 'equals',
        values: [assigneeUsername],
      },
      {
        member: 'TimeEntries.endDate',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: 'TimeEntries.workitemNumber',
        operator: 'set',
      },
    ]),
  };
}

// User Time Tracking - Subtask total time spent by user
export function userCompletedTimeSubItemTimeSpentTotalQuery(
  organizationId: string,
  assigneeUsername: string,
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  const timezone =
    organizationStore.activeOrganization?.timezone || 'America/New_York';

  return {
    measures: ['WorkitemsSubItems.totalSubItemTimeSpentSeconds'],
    timezone,
    filters: getCubeFilters(organizationId, filters, 'WorkitemsSubItems', [
      {
        member: 'WorkitemsSubItems.isSubItemTimeSpent',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'WorkitemsSubItems.subItemAssigneeUsername',
        operator: 'equals',
        values: [assigneeUsername],
      },
      {
        member: 'WorkitemsSubItems.archived',
        operator: 'equals',
        values: ['false'],
      },
      {
        member: 'WorkitemsSubItems.subItemCompletedAt',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ]),
  };
}

// User Time Tracking - Items with time entries by user
export function userCompletedTimeItemTimeSpentQuery(
  assigneeUsername: string,
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: ['TimeEntries.totalLengthSeconds'],
    dimensions: [
      'TimeEntries.id',
      'TimeEntries.workitemTitle',
      'TimeEntries.workitemNumber',
      'TimeEntries.description',
      'TimeEntries.endDate',
      'TimeEntries.workitemCompletedAt',
      'TimeEntries.workTypeId',
    ],
    timezone,
    order: {
      'TimeEntries.endDate': 'desc',
    },
    filters: getCubeFilters(organizationId, filters, 'TimeEntries', [
      {
        member: 'TimeEntries.authorUsername',
        operator: 'equals',
        values: [assigneeUsername],
      },
      {
        member: 'TimeEntries.completed',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'TimeEntries.endDate',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: 'TimeEntries.workitemNumber',
        operator: 'set',
      },
    ]),
  };
  /** helper to map queried rows to ITimeSpentWorkItem */
  const rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem = (
    row
  ) => ({
    type: 'timeSpent',
    number: row['TimeEntries.workitemNumber'],
    title: row['TimeEntries.workitemTitle'],
    description: row['TimeEntries.description'],
    timeSpentSeconds: row['TimeEntries.totalLengthSeconds'],
    timeEntryDate: row['TimeEntries.endDate'],
    completedAt: row['TimeEntries.workitemCompletedAt'],
    workTypeId: row['TimeEntries.workTypeId'],
  });
  return { query, rowMapper };
}

// User Time Tracking - Subtasks completed by user
export function userCompletedTimeSubItemTimeSpentQuery(
  assigneeUsername: string,
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    dimensions: [
      'WorkitemsSubItems.title',
      'WorkitemsSubItems.number',
      'WorkitemsSubItems.subItemTitle',
      'WorkitemsSubItems.subItemTimeSpentSeconds',
      'WorkitemsSubItems.subItemCompletedAt',
      'WorkitemsSubItems.workTypeId',
    ],
    timezone,
    order: {
      'WorkitemsSubItems.subItemCompletedAt': 'desc',
    },
    filters: getCubeFilters(organizationId, filters, 'WorkitemsSubItems', [
      {
        member: 'WorkitemsSubItems.isSubItemTimeSpent',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'WorkitemsSubItems.subItemAssigneeUsername',
        operator: 'equals',
        values: [assigneeUsername],
      },
      {
        member: 'WorkitemsSubItems.archived',
        operator: 'equals',
        values: ['false'],
      },
      {
        member: 'WorkitemsSubItems.subItemCompletedAt',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
    ]),
  };
  /** helper to map queried rows to ITimeSpentWorkItem */
  const rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem = (
    row
  ) => ({
    type: 'timeSpent',
    number: row['WorkitemsSubItems.number'],
    title: row['WorkitemsSubItems.subItemTitle'],
    itemTitle: row['WorkitemsSubItems.title'],
    timeSpentSeconds: row['WorkitemsSubItems.subItemTimeSpentSeconds'],
    timeEntryDate: row['WorkitemsSubItems.subItemCompletedAt'],
    completedAt: row['WorkitemsSubItems.subItemCompletedAt'],
    workTypeId: row['WorkitemsSubItems.workTypeId'],
  });
  return { query, rowMapper };
}

// User Time Tracking - All users' time entries totals (grouped by user)
export function allUsersTimeEntriesTotalQuery(
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  return {
    measures: ['TimeEntries.totalLengthSeconds'],
    dimensions: ['TimeEntries.authorUsername'],
    timezone,
    filters: getTimeEntriesCubeFilters(organizationId, filters, [
      {
        member: 'TimeEntries.completed',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'TimeEntries.endDate',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: 'TimeEntries.workitemNumber',
        operator: 'set',
      },
    ]),
  };
}

// User Time Tracking - All users' subtask time totals (grouped by user)
export function allUsersSubItemTimeSpentTotalQuery(
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
) {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  return {
    measures: ['WorkitemsSubItems.totalSubItemTimeSpentSeconds'],
    dimensions: ['WorkitemsSubItems.subItemAssigneeUsername'],
    timezone,
    filters: getWorkitemsSubItemsCubeFilters(organizationId, filters, [
      {
        member: 'WorkitemsSubItems.isSubItemTimeSpent',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'WorkitemsSubItems.archived',
        operator: 'equals',
        values: ['false'],
      },
      {
        member: 'WorkitemsSubItems.subItemCompletedAt',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: 'WorkitemsSubItems.subItemTimeSpentSeconds',
        operator: 'gt',
        values: ['0'],
      },
    ]),
  };
}

// User Time Tracking - All users' item time entries with details
export function allUsersItemTimeSpentQuery(
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    measures: ['TimeEntries.totalLengthSeconds'],
    dimensions: [
      'TimeEntries.id',
      'TimeEntries.authorUsername',
      'TimeEntries.workitemTitle',
      'TimeEntries.workitemNumber',
      'TimeEntries.description',
      'TimeEntries.endDate',
      'TimeEntries.workitemCompletedAt',
      'TimeEntries.workTypeId',
    ],
    timezone,
    order: {
      'TimeEntries.authorUsername': 'asc',
      'TimeEntries.endDate': 'desc',
    },
    filters: getTimeEntriesCubeFilters(organizationId, filters, [
      {
        member: 'TimeEntries.completed',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'TimeEntries.endDate',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: 'TimeEntries.workitemNumber',
        operator: 'set',
      },
    ]),
  };
  /** helper to map queried rows to ITimeSpentWorkItem */
  const rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem = (
    row
  ) => ({
    type: 'timeSpent',
    authorUsername: row['TimeEntries.authorUsername'],
    number: row['TimeEntries.workitemNumber'],
    title: row['TimeEntries.workitemTitle'],
    description: row['TimeEntries.description'],
    timeSpentSeconds: row['TimeEntries.totalLengthSeconds'],
    timeEntryDate: row['TimeEntries.endDate'],
    completedAt: row['TimeEntries.workitemCompletedAt'],
    workTypeId: row['TimeEntries.workTypeId'],
  });
  return { query, rowMapper };
}

// User Time Tracking - All users' subtask time entries with details
export function allUsersSubItemTimeSpentQuery(
  dateRange: DateTimeRange,
  filters?: StoredQueryOptions
): {
  query: Query;
  rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem;
} {
  const organizationStore = useOrganizationStore();
  if (!organizationStore.activeOrganization)
    throw new Error('Active organization not found');
  const organizationId = organizationStore.activeOrganization.id;
  const timezone =
    organizationStore.activeOrganization.timezone || 'America/New_York';

  const query: Query = {
    dimensions: [
      'WorkitemsSubItems.title',
      'WorkitemsSubItems.number',
      'WorkitemsSubItems.subItemTitle',
      'WorkitemsSubItems.subItemTimeSpentSeconds',
      'WorkitemsSubItems.subItemCompletedAt',
      'WorkitemsSubItems.subItemAssigneeUsername',
      'WorkitemsSubItems.workTypeId',
    ],
    timezone,
    order: {
      'WorkitemsSubItems.subItemAssigneeUsername': 'asc',
      'WorkitemsSubItems.subItemCompletedAt': 'desc',
    },
    filters: getWorkitemsSubItemsCubeFilters(organizationId, filters, [
      {
        member: 'WorkitemsSubItems.isSubItemTimeSpent',
        operator: 'equals',
        values: ['true'],
      },
      {
        member: 'WorkitemsSubItems.archived',
        operator: 'equals',
        values: ['false'],
      },
      {
        member: 'WorkitemsSubItems.subItemCompletedAt',
        operator: 'inDateRange',
        values: [
          dayjs(dateRange.from).format('YYYY-MM-DD'),
          dayjs(dateRange.to).format('YYYY-MM-DD'),
        ],
      },
      {
        member: 'WorkitemsSubItems.subItemTimeSpentSeconds',
        operator: 'gt',
        values: ['0'],
      },
    ]),
  };
  /** helper to map queried rows to ITimeSpentWorkItem */
  const rowMapper: (row: Record<string, string>) => ITimeSpentWorkItem = (
    row
  ) => ({
    type: 'timeSpent',
    // For subtasks, we use the assignee as the "author" since subtask completion
    // is attributed to whoever was assigned to complete the subtask
    authorUsername: row['WorkitemsSubItems.subItemAssigneeUsername'],
    number: row['WorkitemsSubItems.number'],
    title: row['WorkitemsSubItems.subItemTitle'],
    itemTitle: row['WorkitemsSubItems.title'],
    timeSpentSeconds: row['WorkitemsSubItems.subItemTimeSpentSeconds'],
    timeEntryDate: row['WorkitemsSubItems.subItemCompletedAt'],
    completedAt: row['WorkitemsSubItems.subItemCompletedAt'],
    workTypeId: row['WorkitemsSubItems.workTypeId'],
  });
  return { query, rowMapper };
}

function processAdvancedFilterGroup(
  group: AdvancedFilterGroup,
  baseFilters: Omit<StoredQueryOptions, 'advancedFilters'>,
  tableToUse: DSCubeTableName
): Filter | null {
  const allFilters: Filter[] = [];
  const operator = group.operator === 'all' ? 'and' : 'or';

  // Process children
  for (const child of group.children) {
    if (child.type === 'rule') {
      const field = child.field as keyof StoredQueryOptions;
      const singleRuleOptions: Partial<StoredQueryOptions> = {
        [field]: child.value as StoredQueryOptions[typeof field],
      };
      const ruleFilter = itemFiltersToCubeFilters(
        singleRuleOptions,
        tableToUse
      );
      if (ruleFilter.length) {
        allFilters.push(...ruleFilter);
      }
    } else if (child.type === 'group') {
      const nestedFilter = processAdvancedFilterGroup(
        child,
        baseFilters,
        tableToUse
      );
      if (nestedFilter) {
        allFilters.push(nestedFilter);
      }
    }
  }

  // Combine all filters with the appropriate operator
  if (allFilters.length === 0) {
    return null;
  } else if (allFilters.length === 1) {
    return allFilters[0];
  } else {
    return {
      [operator]: allFilters,
    } as Filter;
  }
}

/**
 * Converts a ConditionSet to Cube filter format
 */
function conditionSetToCubeFilter(
  conditionSet: ConditionSet,
  member: string
): Filter | null {
  const conditions = conditionSet.any || conditionSet.all || [];
  if (conditions.length === 0) return null;

  const cubeOperatorMap: Record<
    ConditionOperator,
    BinaryOperator | UnaryOperator
  > = {
    equal: 'equals',
    notEqual: 'notEquals',
    in: 'equals',
    notIn: 'notEquals',
    notSet: 'notSet',
    isSet: 'set',
    contains: 'contains',
    doesNotContain: 'notContains',
    lessThan: 'lt',
    lessThanInclusive: 'lte',
    greaterThan: 'gt',
    greaterThanInclusive: 'gte',
    assigneeInSubItems: 'equals', // Special case, handle separately if needed
  };

  const filters: Filter[] = [];

  for (const condition of conditions) {
    // Skip ASSIGNEE_IN_SUB_ITEMS as Cube doesn't support JSONB sub-queries
    // This operator is handled at the PostgreSQL level, not in analytics
    if (condition.operator === 'assigneeInSubItems') {
      continue;
    }

    const cubeOperator = cubeOperatorMap[condition.operator];
    if (!cubeOperator) continue;

    // Handle unary operators (notSet, isSet)
    if (condition.operator === 'notSet' || condition.operator === 'isSet') {
      filters.push({
        member,
        operator: cubeOperator as UnaryOperator,
      });
      continue;
    }

    // Handle binary operators with values
    if (condition.value !== null && condition.value !== undefined) {
      const values = Array.isArray(condition.value)
        ? condition.value.map(String)
        : [String(condition.value)];

      filters.push({
        member,
        operator: cubeOperator as BinaryOperator,
        values,
      });
    }
  }

  if (filters.length === 0) return null;
  if (filters.length === 1) return filters[0];

  // Combine with AND or OR based on conditionSet type
  if (conditionSet.any) {
    return { or: filters };
  } else {
    return { and: filters };
  }
}

function itemFiltersToCubeFilters(
  filters: StoredQueryOptions,
  tableToUse: DSCubeTableName
) {
  const cubeFilters: Filter[] = [];

  if (filters.workTypeId?.length) {
    cubeFilters.push({
      member: 'Workitems.workTypeId',
      operator: 'equals',
      values: filters.workTypeId,
    });
  }

  if (filters.hierarchy) {
    cubeFilters.push({
      member: 'Workitems.hierarchyPath',
      operator: 'contains',
      values: filters.hierarchy.map((h) => h + ':'),
    });
  }

  if (filters.assigneeUsername?.length || filters.isUnassigned) {
    const assigneeFilterObj: Filter | undefined = filters.assigneeUsername
      ?.length
      ? {
          member: `${tableToUse}.assigneeUsername`,
          operator: 'equals',
          values: filters.assigneeUsername,
        }
      : undefined;

    const isUnassignedFilterObj: Filter | undefined = filters.isUnassigned
      ? {
          member: `${tableToUse}.assigneeUsername`,
          operator: 'notSet',
        }
      : undefined;

    if (assigneeFilterObj && isUnassignedFilterObj) {
      cubeFilters.push({
        or: [assigneeFilterObj, isUnassignedFilterObj],
      });
    } else if (assigneeFilterObj) {
      cubeFilters.push(assigneeFilterObj);
    } else if (isUnassignedFilterObj) {
      cubeFilters.push(isUnassignedFilterObj);
    }
  }

  if (filters.authorUsername?.length) {
    cubeFilters.push({
      member: 'Workitems.authorUsername',
      operator: 'equals',
      values: filters.authorUsername,
    });
  }

  if (filters.priority?.length) {
    cubeFilters.push({
      member: 'Workitems.priorityId',
      operator: 'equals',
      values: filters.priority,
    });
  }

  if (filters.labels?.length) {
    cubeFilters.push({
      member: 'Workitems.labels',
      operator: 'contains',
      values: filters.labels,
    });
  }

  if (filters.customFields && Object.keys(filters.customFields || {}).length) {
    const organizationStore = useOrganizationStore();

    /**
     * Maps the base operators to the cube operators (binary)
     */
    const customFieldBinaryOperatorMap: {
      [key in ConditionOperator]?: BinaryOperator;
    } = {
      equal: 'equals',
      notEqual: 'notEquals',
      in: 'contains',
      notIn: 'notContains',
      contains: 'contains',
      doesNotContain: 'notContains',
      lessThan: 'lt',
      lessThanInclusive: 'lte',
      greaterThan: 'gt',
      greaterThanInclusive: 'gte',
    };
    /**
     * Maps the base operators to the cube operators (unary)
     */
    const customFieldUnaryOperatorMap: {
      [key in ConditionOperator]?: UnaryOperator;
    } = {
      isSet: 'set',
      notSet: 'notSet',
    };
    const customFieldFilter =
      typeof filters.customFields === 'string'
        ? decodeBase64<CustomFieldConditions>(filters.customFields)
        : filters.customFields;

    Object.entries(customFieldFilter || {}).forEach(([key, value]) => {
      // prepare vars
      const customField =
        organizationStore.activeOrganization?.customFields.find(
          (cf) => cf.id === key
        );
      if (!customField) return;
      const conditions = value.all || value.any || [];

      // build filters
      const ret: Filter[] = [];
      conditions.forEach((v) => {
        const member = `Workitems.field${customField.id.replaceAll('-', '')}`;

        const unaryOperator = customFieldUnaryOperatorMap[v.operator];
        if (unaryOperator) {
          ret.push({
            member,
            operator: unaryOperator,
          });
        }

        const binaryOperator = customFieldBinaryOperatorMap[v.operator];
        if (binaryOperator) {
          const values = (Array.isArray(v.value) ? v.value : [v.value]).map(
            (v) => (isNil(v) ? '' : v.toString())
          );
          if (['contains', 'notContains'].includes(binaryOperator)) {
            const valueConditions = values.map((v) => ({
              member,
              operator: binaryOperator,
              values: [v],
            }));
            ret.push({
              or: valueConditions,
            });
          } else {
            ret.push({
              member,
              operator: binaryOperator,
              values,
            });
          }
        }

        if (!unaryOperator && !binaryOperator)
          throw new Error(`unknown operator ${v.operator}`);
      });

      // add to cube filters
      if (value.all) {
        cubeFilters.push(...ret);
      } else if (value.any) {
        cubeFilters.push({
          or: ret,
        });
      } else {
        throw new Error('neither all nor any, such can not be');
      }
    });
  }

  if (filters.teamId?.length || filters.hasNoTeam) {
    const teamFilterObj: Filter | undefined = filters.teamId?.length
      ? {
          member: `${tableToUse}.teamId`,
          operator: 'equals',
          values: filters.teamId,
        }
      : undefined;

    const hasNoTeamFilterObj: Filter | undefined = filters.hasNoTeam
      ? {
          member: `${tableToUse}.teamId`,
          operator: 'notSet',
        }
      : undefined;

    if (teamFilterObj && hasNoTeamFilterObj) {
      cubeFilters.push({
        or: [teamFilterObj, hasNoTeamFilterObj],
      });
    } else if (teamFilterObj) {
      cubeFilters.push(teamFilterObj);
    } else if (hasNoTeamFilterObj) {
      cubeFilters.push(hasNoTeamFilterObj);
    }
  }

  if (filters.boardId?.length || filters.hasNoBoard) {
    const boardFilterObj: Filter | undefined = filters.boardId?.length
      ? {
          member: `${tableToUse}.boardId`,
          operator: 'equals',
          values: filters.boardId,
        }
      : undefined;

    const hasNoBoardFilterObj: Filter | undefined = filters.hasNoBoard
      ? {
          member: `${tableToUse}.boardId`,
          operator: 'notSet',
        }
      : undefined;

    if (boardFilterObj && hasNoBoardFilterObj) {
      cubeFilters.push({
        or: [boardFilterObj, hasNoBoardFilterObj],
      });
    } else if (boardFilterObj) {
      cubeFilters.push(boardFilterObj);
    } else if (hasNoBoardFilterObj) {
      cubeFilters.push(hasNoBoardFilterObj);
    }
  }

  if (filters.laneId?.length) {
    cubeFilters.push({
      member: `${tableToUse}.laneId`,
      operator: 'equals',
      values: filters.laneId,
    });
  }

  if (filters.dueDateLTE) {
    const nextDay = dayjs(filters.dueDateLTE)
      .add(1, 'day')
      .format('YYYY-MM-DD');
    cubeFilters.push({
      member: 'Workitems.dueDate',
      operator: 'beforeDate',
      values: [nextDay],
    });
  }

  if (filters.dueDateGTE) {
    const previousDay = dayjs(filters.dueDateGTE)
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    cubeFilters.push({
      member: 'Workitems.dueDate',
      operator: 'afterDate',
      values: [previousDay],
    });
  }

  if (filters.startDateLTE) {
    const nextDay = dayjs(filters.startDateLTE)
      .add(1, 'day')
      .format('YYYY-MM-DD');
    cubeFilters.push({
      member: 'Workitems.startDate',
      operator: 'beforeDate',
      values: [nextDay],
    });
  }

  if (filters.startDateGTE) {
    const previousDay = dayjs(filters.startDateGTE)
      .subtract(1, 'day')
      .format('YYYY-MM-DD');
    cubeFilters.push({
      member: 'Workitems.startDate',
      operator: 'afterDate',
      values: [previousDay],
    });
  }

  if (filters.completedAtGTE || filters.completedAtLTE) {
    if (filters.completedAtGTE && filters.completedAtLTE) {
      const startDate = dayjs(filters.completedAtGTE).toISOString();
      const endDate = dayjs(filters.completedAtLTE).toISOString();

      cubeFilters.push({
        member: 'Workitems.completedAt',
        operator: 'inDateRange',
        values: [startDate, endDate],
      });
    } else {
      if (filters.completedAtLTE) {
        const endDate = dayjs(filters.completedAtLTE)
          .add(1, 'millisecond')
          .toISOString();

        cubeFilters.push({
          member: 'Workitems.completedAt',
          operator: 'beforeDate',
          values: [endDate],
        });
      }
      if (filters.completedAtGTE) {
        const startDate = dayjs(filters.completedAtGTE)
          .subtract(1, 'millisecond')
          .toISOString();

        cubeFilters.push({
          member: 'Workitems.completedAt',
          operator: 'afterDate',
          values: [startDate],
        });
      }
    }
  }

  if (!isNil(filters.timeSpentSecondsGTE)) {
    cubeFilters.push({
      member: `Workitems.timeSpentSeconds`,
      operator: 'gte',
      values: [filters.timeSpentSecondsGTE.toString()],
    });
  } else if (!isNil(filters.timeSpentSecondsLTE)) {
    cubeFilters.push({
      member: `Workitems.timeSpentSeconds`,
      operator: 'lte',
      values: [filters.timeSpentSecondsLTE.toString()],
    });
  }

  if (filters.watchers?.length) {
    cubeFilters.push({
      member: 'Workitems.watchers',
      operator: 'contains',
      values: filters.watchers,
    });
  }

  if (filters.teamConditions) {
    const teamFilter = conditionSetToCubeFilter(
      filters.teamConditions,
      `${tableToUse}.teamId`
    );
    if (teamFilter) cubeFilters.push(teamFilter);
  }

  if (filters.assigneeConditions) {
    const assigneeFilter = conditionSetToCubeFilter(
      filters.assigneeConditions,
      `${tableToUse}.assigneeUsername`
    );
    if (assigneeFilter) cubeFilters.push(assigneeFilter);
  }

  if (filters.boardConditions) {
    const boardFilter = conditionSetToCubeFilter(
      filters.boardConditions,
      `${tableToUse}.boardId`
    );
    if (boardFilter) cubeFilters.push(boardFilter);
  }

  if (filters.dueDateConditions) {
    const dueDateFilter = conditionSetToCubeFilter(
      filters.dueDateConditions,
      'Workitems.dueDate'
    );
    if (dueDateFilter) cubeFilters.push(dueDateFilter);
  }

  if (filters.startDateConditions) {
    const startDateFilter = conditionSetToCubeFilter(
      filters.startDateConditions,
      'Workitems.startDate'
    );
    if (startDateFilter) cubeFilters.push(startDateFilter);
  }

  if (filters.completedAtConditions) {
    const completedAtFilter = conditionSetToCubeFilter(
      filters.completedAtConditions,
      'Workitems.completedAt'
    );
    if (completedAtFilter) cubeFilters.push(completedAtFilter);
  }

  return cubeFilters;
}

function getCubeFilters(
  organizationId: string,
  itemFilters?: StoredQueryOptions,
  tableToUse: DSCubeTableName = 'Workitems',
  cubeFilters: Filter[] = []
): Filter[] {
  const filters: Filter = {
    and: [
      {
        member: `${tableToUse}.organizationId`,
        operator: 'equals',
        values: [organizationId],
      },
      ...cubeFilters,
    ],
  };

  if (!itemFilters) return [filters];

  const { advancedFilters, ...otherFilters } = itemFilters || {};

  if (!otherFilters.includeArchived) {
    filters.and.push({
      member: 'Workitems.archived',
      operator: 'equals',
      values: ['false'],
    });
  }

  if (otherFilters) {
    const otherFiltersObj = itemFiltersToCubeFilters(otherFilters, tableToUse);
    filters.and.push(...otherFiltersObj);
  }

  if (advancedFilters) {
    const filterGroup =
      typeof advancedFilters === 'string'
        ? decodeBase64<AdvancedFilterGroup>(advancedFilters)
        : advancedFilters;

    if (filterGroup) {
      const advancedFilter = processAdvancedFilterGroup(
        filterGroup,
        otherFilters,
        tableToUse
      );
      if (advancedFilter) {
        filters.and.push(advancedFilter);
      }
    }
  }

  return [filters];
}

/**
 * Converts StoredQueryOptions to Cube filters for TimeEntries table.
 * Only includes filters that have corresponding dimensions in the TimeEntries Cube schema.
 *
 * Supported filters:
 * - teamId / hasNoTeam -> TimeEntries.teamId
 * - boardId / hasNoBoard -> TimeEntries.boardId
 * - laneId -> TimeEntries.laneId
 * - workTypeId -> TimeEntries.workTypeId
 * - includeArchived -> TimeEntries.archived
 * - lengthSecondsGTE / lengthSecondsLTE -> TimeEntries.lengthSeconds (individual entry duration)
 * - priority -> TimeEntries.priorityId
 * - hierarchy / hasNoParent -> TimeEntries.hierarchyPath / TimeEntries.parentNumber
 * - assigneeUsername / isUnassigned -> TimeEntries.workitemAssigneeUsername
 * - startDateGTE/LTE -> TimeEntries.workitemStartDate
 * - dueDateGTE/LTE -> TimeEntries.workitemDueDate
 *
 * NOT supported (would require additional Workitems columns in join):
 * - labels, customFields, authorUsername (item author), watchers
 * - timeSpentSecondsGTE/LTE (item's total time spent, not individual entry duration)
 */
function timeEntriesFiltersToCubeFilters(
  filters: StoredQueryOptions
): Filter[] {
  const cubeFilters: Filter[] = [];

  if (filters.workTypeId?.length) {
    cubeFilters.push({
      member: 'TimeEntries.workTypeId',
      operator: 'equals',
      values: filters.workTypeId,
    });
  }

  if (filters.teamId?.length || filters.hasNoTeam) {
    const teamFilterObj: Filter | undefined = filters.teamId?.length
      ? {
          member: 'TimeEntries.teamId',
          operator: 'equals',
          values: filters.teamId,
        }
      : undefined;

    const hasNoTeamFilterObj: Filter | undefined = filters.hasNoTeam
      ? {
          member: 'TimeEntries.teamId',
          operator: 'notSet',
        }
      : undefined;

    if (teamFilterObj && hasNoTeamFilterObj) {
      cubeFilters.push({
        or: [teamFilterObj, hasNoTeamFilterObj],
      });
    } else if (teamFilterObj) {
      cubeFilters.push(teamFilterObj);
    } else if (hasNoTeamFilterObj) {
      cubeFilters.push(hasNoTeamFilterObj);
    }
  }

  if (filters.boardId?.length || filters.hasNoBoard) {
    const boardFilterObj: Filter | undefined = filters.boardId?.length
      ? {
          member: 'TimeEntries.boardId',
          operator: 'equals',
          values: filters.boardId,
        }
      : undefined;

    const hasNoBoardFilterObj: Filter | undefined = filters.hasNoBoard
      ? {
          member: 'TimeEntries.boardId',
          operator: 'notSet',
        }
      : undefined;

    if (boardFilterObj && hasNoBoardFilterObj) {
      cubeFilters.push({
        or: [boardFilterObj, hasNoBoardFilterObj],
      });
    } else if (boardFilterObj) {
      cubeFilters.push(boardFilterObj);
    } else if (hasNoBoardFilterObj) {
      cubeFilters.push(hasNoBoardFilterObj);
    }
  }

  if (filters.laneId?.length) {
    cubeFilters.push({
      member: 'TimeEntries.laneId',
      operator: 'equals',
      values: filters.laneId,
    });
  }

  // Priority filter
  if (filters.priority?.length) {
    cubeFilters.push({
      member: 'TimeEntries.priorityId',
      operator: 'equals',
      values: filters.priority,
    });
  }

  // Hierarchy filter - filters by ancestor hierarchy path (greedy match)
  if (filters.hierarchy?.length || filters.hasNoParent) {
    const hierarchyFilterObj: Filter | undefined = filters.hierarchy?.length
      ? {
          member: 'TimeEntries.hierarchyPath',
          operator: 'contains',
          values: filters.hierarchy.map((h) => h + ':'),
        }
      : undefined;

    const hasNoParentFilterObj: Filter | undefined = filters.hasNoParent
      ? {
          member: 'TimeEntries.parentNumber',
          operator: 'notSet',
        }
      : undefined;

    if (hierarchyFilterObj && hasNoParentFilterObj) {
      cubeFilters.push({
        or: [hierarchyFilterObj, hasNoParentFilterObj],
      });
    } else if (hierarchyFilterObj) {
      cubeFilters.push(hierarchyFilterObj);
    } else if (hasNoParentFilterObj) {
      cubeFilters.push(hasNoParentFilterObj);
    }
  }

  // Length filter - filters individual time entries by their duration
  // Uses TimeEntries.lengthSeconds dimension
  if (!isNil(filters.lengthSecondsGTE)) {
    cubeFilters.push({
      member: 'TimeEntries.lengthSeconds',
      operator: 'gte',
      values: [filters.lengthSecondsGTE.toString()],
    });
  }

  if (!isNil(filters.lengthSecondsLTE)) {
    cubeFilters.push({
      member: 'TimeEntries.lengthSeconds',
      operator: 'lte',
      values: [filters.lengthSecondsLTE.toString()],
    });
  }

  // Assignee filter - filters by the workitem's assignee
  if (filters.assigneeUsername?.length || filters.isUnassigned) {
    const assigneeFilterObj: Filter | undefined = filters.assigneeUsername
      ?.length
      ? {
          member: 'TimeEntries.workitemAssigneeUsername',
          operator: 'equals',
          values: filters.assigneeUsername,
        }
      : undefined;

    const isUnassignedFilterObj: Filter | undefined = filters.isUnassigned
      ? {
          member: 'TimeEntries.workitemAssigneeUsername',
          operator: 'notSet',
        }
      : undefined;

    if (assigneeFilterObj && isUnassignedFilterObj) {
      cubeFilters.push({
        or: [assigneeFilterObj, isUnassignedFilterObj],
      });
    } else if (assigneeFilterObj) {
      cubeFilters.push(assigneeFilterObj);
    } else if (isUnassignedFilterObj) {
      cubeFilters.push(isUnassignedFilterObj);
    }
  }

  // Date filters - filter by workitem's start date and due date
  if (filters.startDateGTE) {
    cubeFilters.push({
      member: 'TimeEntries.workitemStartDate',
      operator: 'gte',
      values: [filters.startDateGTE],
    });
  }

  if (filters.startDateLTE) {
    cubeFilters.push({
      member: 'TimeEntries.workitemStartDate',
      operator: 'lte',
      values: [filters.startDateLTE],
    });
  }

  if (filters.dueDateGTE) {
    cubeFilters.push({
      member: 'TimeEntries.workitemDueDate',
      operator: 'gte',
      values: [filters.dueDateGTE],
    });
  }

  if (filters.dueDateLTE) {
    cubeFilters.push({
      member: 'TimeEntries.workitemDueDate',
      operator: 'lte',
      values: [filters.dueDateLTE],
    });
  }

  return cubeFilters;
}

/**
 * Gets Cube filters for TimeEntries table queries.
 * Uses timeEntriesFiltersToCubeFilters for filter mapping since TimeEntries
 * has its own dimensions (teamId, boardId, laneId, workTypeId) that don't
 * require joins to the Workitems table.
 */
function getTimeEntriesCubeFilters(
  organizationId: string,
  itemFilters?: StoredQueryOptions,
  cubeFilters: Filter[] = []
): Filter[] {
  const filters: Filter = {
    and: [
      {
        member: 'TimeEntries.organizationId',
        operator: 'equals',
        values: [organizationId],
      },
      ...cubeFilters,
    ],
  };

  if (!itemFilters) return [filters];

  // Add archived filter
  if (!itemFilters.includeArchived) {
    filters.and.push({
      member: 'TimeEntries.archived',
      operator: 'equals',
      values: ['false'],
    });
  }

  // Add TimeEntries-specific filters
  const timeEntriesFilters = timeEntriesFiltersToCubeFilters(itemFilters);
  filters.and.push(...timeEntriesFilters);

  return [filters];
}

/**
 * Gets Cube filters for WorkitemsSubItems table queries.
 * WorkitemsSubItems has dimensions from the parent workitem available for filtering.
 *
 * Supported filters:
 * - workTypeId -> WorkitemsSubItems.workTypeId
 * - teamId / hasNoTeam -> WorkitemsSubItems.teamId
 * - boardId / hasNoBoard -> WorkitemsSubItems.boardId
 * - laneId -> WorkitemsSubItems.laneId
 * - priority -> WorkitemsSubItems.priorityId
 * - hierarchy / hasNoParent -> WorkitemsSubItems.hierarchyPath / WorkitemsSubItems.parentNumber
 * - assigneeUsername / isUnassigned -> WorkitemsSubItems.assigneeUsername
 * - startDateGTE/LTE -> WorkitemsSubItems.startDate
 * - dueDateGTE/LTE -> WorkitemsSubItems.dueDate
 *
 * NOT supported (no dimensions available):
 * - labels, customFields, watchers, authorUsername (item author)
 */
function getWorkitemsSubItemsCubeFilters(
  organizationId: string,
  itemFilters?: StoredQueryOptions,
  cubeFilters: Filter[] = []
): Filter[] {
  const filters: Filter = {
    and: [
      {
        member: 'WorkitemsSubItems.organizationId',
        operator: 'equals',
        values: [organizationId],
      },
      ...cubeFilters,
    ],
  };

  if (!itemFilters) return [filters];

  if (itemFilters.workTypeId?.length) {
    filters.and.push({
      member: 'WorkitemsSubItems.workTypeId',
      operator: 'equals',
      values: itemFilters.workTypeId,
    });
  }

  // Team filter
  if (itemFilters.teamId?.length || itemFilters.hasNoTeam) {
    const teamFilterObj: Filter | undefined = itemFilters.teamId?.length
      ? {
          member: 'WorkitemsSubItems.teamId',
          operator: 'equals',
          values: itemFilters.teamId,
        }
      : undefined;

    const hasNoTeamFilterObj: Filter | undefined = itemFilters.hasNoTeam
      ? {
          member: 'WorkitemsSubItems.teamId',
          operator: 'notSet',
        }
      : undefined;

    if (teamFilterObj && hasNoTeamFilterObj) {
      filters.and.push({
        or: [teamFilterObj, hasNoTeamFilterObj],
      });
    } else if (teamFilterObj) {
      filters.and.push(teamFilterObj);
    } else if (hasNoTeamFilterObj) {
      filters.and.push(hasNoTeamFilterObj);
    }
  }

  // Board filter
  if (itemFilters.boardId?.length || itemFilters.hasNoBoard) {
    const boardFilterObj: Filter | undefined = itemFilters.boardId?.length
      ? {
          member: 'WorkitemsSubItems.boardId',
          operator: 'equals',
          values: itemFilters.boardId,
        }
      : undefined;

    const hasNoBoardFilterObj: Filter | undefined = itemFilters.hasNoBoard
      ? {
          member: 'WorkitemsSubItems.boardId',
          operator: 'notSet',
        }
      : undefined;

    if (boardFilterObj && hasNoBoardFilterObj) {
      filters.and.push({
        or: [boardFilterObj, hasNoBoardFilterObj],
      });
    } else if (boardFilterObj) {
      filters.and.push(boardFilterObj);
    } else if (hasNoBoardFilterObj) {
      filters.and.push(hasNoBoardFilterObj);
    }
  }

  // Lane filter
  if (itemFilters.laneId?.length) {
    filters.and.push({
      member: 'WorkitemsSubItems.laneId',
      operator: 'equals',
      values: itemFilters.laneId,
    });
  }

  // Priority filter
  if (itemFilters.priority?.length) {
    filters.and.push({
      member: 'WorkitemsSubItems.priorityId',
      operator: 'equals',
      values: itemFilters.priority,
    });
  }

  // Hierarchy filter - filters by ancestor hierarchy path (greedy match)
  if (itemFilters.hierarchy?.length || itemFilters.hasNoParent) {
    const hierarchyFilterObj: Filter | undefined = itemFilters.hierarchy?.length
      ? {
          member: 'WorkitemsSubItems.hierarchyPath',
          operator: 'contains',
          values: itemFilters.hierarchy.map((h) => h + ':'),
        }
      : undefined;

    const hasNoParentFilterObj: Filter | undefined = itemFilters.hasNoParent
      ? {
          member: 'WorkitemsSubItems.parentNumber',
          operator: 'notSet',
        }
      : undefined;

    if (hierarchyFilterObj && hasNoParentFilterObj) {
      filters.and.push({
        or: [hierarchyFilterObj, hasNoParentFilterObj],
      });
    } else if (hierarchyFilterObj) {
      filters.and.push(hierarchyFilterObj);
    } else if (hasNoParentFilterObj) {
      filters.and.push(hasNoParentFilterObj);
    }
  }

  // Assignee filter - filters by the parent workitem's assignee
  if (itemFilters.assigneeUsername?.length || itemFilters.isUnassigned) {
    const assigneeFilterObj: Filter | undefined = itemFilters.assigneeUsername
      ?.length
      ? {
          member: 'WorkitemsSubItems.assigneeUsername',
          operator: 'equals',
          values: itemFilters.assigneeUsername,
        }
      : undefined;

    const isUnassignedFilterObj: Filter | undefined = itemFilters.isUnassigned
      ? {
          member: 'WorkitemsSubItems.assigneeUsername',
          operator: 'notSet',
        }
      : undefined;

    if (assigneeFilterObj && isUnassignedFilterObj) {
      filters.and.push({
        or: [assigneeFilterObj, isUnassignedFilterObj],
      });
    } else if (assigneeFilterObj) {
      filters.and.push(assigneeFilterObj);
    } else if (isUnassignedFilterObj) {
      filters.and.push(isUnassignedFilterObj);
    }
  }

  // Date filters - filter by parent workitem's start date and due date
  if (itemFilters.startDateGTE) {
    filters.and.push({
      member: 'WorkitemsSubItems.startDate',
      operator: 'gte',
      values: [itemFilters.startDateGTE],
    });
  }

  if (itemFilters.startDateLTE) {
    filters.and.push({
      member: 'WorkitemsSubItems.startDate',
      operator: 'lte',
      values: [itemFilters.startDateLTE],
    });
  }

  if (itemFilters.dueDateGTE) {
    filters.and.push({
      member: 'WorkitemsSubItems.dueDate',
      operator: 'gte',
      values: [itemFilters.dueDateGTE],
    });
  }

  if (itemFilters.dueDateLTE) {
    filters.and.push({
      member: 'WorkitemsSubItems.dueDate',
      operator: 'lte',
      values: [itemFilters.dueDateLTE],
    });
  }

  return [filters];
}

function dateRangeToTimeCubeTimeDimensions(
  dateRange: DateTimeRange,
  dateGranularity: 'day' | 'week' | 'month' | undefined,
  dimension: string
): Array<TimeDimension> {
  const { from, to } = dateRange;
  return [
    {
      dimension,
      granularity: dateGranularity,
      dateRange: [
        dayjs(from).format('YYYY-MM-DD'),
        dayjs(to).format('YYYY-MM-DD'),
      ],
    },
  ];
}

export function getDateGranularityFromRange(dateRange: DateTimeRange) {
  const { from, to } = dateRange;
  const dayLength = dayjs(to).diff(dayjs(from), 'day');
  let granularity: 'day' | 'week' | 'month' = 'day';
  if (dayLength > 92) {
    granularity = 'month';
  } else if (dayLength > 31) {
    granularity = 'week';
  }
  return granularity;
}
